import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  getUserDateFormat,
  getUserTimeZone,
} from '../../../common/utils/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Comparator function to sort sensors by priority (1 = primary first), then by sensor ID.
 * Matches the sorting logic used in ShipmentVitals_CT.tsx.
 *
 * @param a - First item with priority and id properties
 * @param b - Second item with priority and id properties
 * @returns Comparison result for Array.sort()
 */
export const sortSensorsByPriorityAndId = <
  T extends { priority?: number; id?: string },
>(
  a: T,
  b: T
): number => {
  const aPriority = a.priority;
  const bPriority = b.priority;
  const aId = a.id || '';
  const bId = b.id || '';

  // Sort by priority first
  if (aPriority && bPriority) {
    return aPriority - bPriority;
  }
  if (aPriority) return -1;
  if (bPriority) return 1;
  // If no priority, sort by sensor ID
  return aId.localeCompare(bId);
};

const tzFormatters = new Map<string, Intl.DateTimeFormat>();

/** Cached per timezone; constructing these per data point is measurably slow. */
const getTzFormatter = (tz: string): Intl.DateTimeFormat => {
  const cached = tzFormatters.get(tz);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  tzFormatters.set(tz, formatter);
  return formatter;
};

/** Offset to add to a true instant so UTC getters read account-timezone wall clock. */
const getTzOffsetMs = (instantMs: number, tz: string): number => {
  const parts = getTzFormatter(tz).formatToParts(new Date(instantMs));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second')
  );
  // Diff against the whole second so sub-second precision survives the round trip.
  return asUtc - Math.floor(instantMs / 1000) * 1000;
};

/**
 * Convert a true epoch instant to chart display ms.
 * ApexCharts can only format its axis in UTC or browser-local, so we offset the value
 * and render with `datetimeUTC: true` — its UTC getters then print account-timezone
 * wall clock regardless of the viewer's browser timezone. Offset is computed per
 * instant, so labels stay correct across DST transitions.
 */
export const instantToChartDisplayMs = (instantMs: number): number =>
  instantMs + getTzOffsetMs(instantMs, getUserTimeZone().name);

/**
 * Parse a timestamp into chart display ms (see `instantToChartDisplayMs`).
 * This is NOT a true instant — it is offset for display and must only be used in chart
 * coordinate space, never sent back to the API or compared against real time.
 */
export const toChartDisplayMs = (timestamp?: string | null) => {
  if (!timestamp) return null;
  const trimmed = timestamp.trim();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) {
    return instantToChartDisplayMs(Number(trimmed));
  }

  const hasTimezone = /[zZ]$/.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed);
  if (hasTimezone) {
    const parsed = new Date(trimmed).getTime();
    return Number.isNaN(parsed) ? null : instantToChartDisplayMs(parsed);
  }

  // No timezone: backend already sends the account's configured timezone, so the string
  // is account wall clock — parsing it as UTC yields display ms with no offset math.
  const normalized = trimmed.includes('T')
    ? trimmed
    : trimmed.replace(' ', 'T');
  const d = dayjs.utc(normalized);
  if (!d.isValid()) return null;
  return d.valueOf();
};

/**
 * Format a chart display ms value for the sensor point tooltip, e.g. "yyyy-MM-dd HH:mm"
 * (date order follows the user's date_format preference; dash-separated).
 * The timezone offset was already applied when the value entered chart space, so this
 * reads it with UTC getters — re-applying the timezone here would double-shift it.
 */
export const formatSensorPointTimestampForTooltip = (
  ms: number,
  dateFormat?: string | null
): string => {
  if (!Number.isFinite(ms)) return '';
  const d = dayjs.utc(ms);
  if (!d.isValid()) return '';
  const year = d.format('YYYY');
  const month = d.format('MM');
  const day = d.format('DD');
  const hour = d.format('HH');
  const minute = d.format('mm');
  const preference = dateFormat || getUserDateFormat();
  const datePart =
    preference === 'DD/MM/YYYY'
      ? `${day}-${month}-${year}`
      : preference === 'YYYY/MM/DD'
        ? `${year}-${month}-${day}`
        : `${month}-${day}-${year}`;
  return `${datePart} ${hour}:${minute}`;
};
